import React, { useState, useEffect, RefObject, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { helperService } from "src/services/helper.service";

import "./Dropdown.scss";

import checkIcon from "../../assets/icons/check.svg";

export interface IOption {
  value: string,
  id: string,
  disabled?: boolean,
  data?: any
}

interface IProps {
  label: string,
  options: IOption[],
  icon?: string,
  placeholder: string,
  value: IOption,
  onChange(e: any, value: IOption): void
  error?: boolean,
  dropUp?: boolean,
  alignRight?: boolean,
  id?: string,
  disabled?: boolean,
  className?: string,
  searchable?: boolean,
  searchPlaceholder?: string
}

export const Provinces:IOption[] = [
  { value: 'Alberta', id: 'AB' },
  { value: 'British Columbia', id: 'BC' },
  { value: 'Manitoba', id: 'MB' },
  { value: 'New Brunswick', id: 'NB' },
  { value: 'Newfoundland and Labrador', id: 'NL' },
  { value: 'Northwest Territories', id: 'NT' },
  { value: 'Nova Scotia', id: 'NS' },
  { value: 'Nunavut', id: 'NU' },
  { value: 'Ontario', id: 'ON' },
  { value: 'Prince Edward Island', id: 'PE' },
  { value: 'Quebec', id: 'QC' },
  { value: 'Saskatchewan', id: 'SK' },
  { value: 'Yukon Territory', id: 'YT' }
];

export const States:IOption[] = [
    { value: 'Alabama', id: 'AL'},
    { value: 'Alaska', id: 'AK'},
    { value: 'American Samoa', id: 'AS'},
    { value: 'Arizona', id: 'AZ'},
    { value: 'Arkansas', id: 'AR'},
    { value: 'California', id: 'CA'},
    { value: 'Colorado', id: 'CO'},
    { value: 'Connecticut', id: 'CT'},
    { value: 'Delaware', id: 'DE'},
    { value: 'District of Colombia', id: 'DC'},
    { value: 'Federated states OF Micronesia', id: 'FM'},
    { value: 'Florida', id: 'FL'},
    { value: 'Georgia', id: 'GA'},
    { value: 'Guam', id: 'GU'},
    { value: 'Hawaii', id: 'HI'},
    { value: 'Idaho', id: 'ID'},
    { value: 'Illinos', id: 'IL'},
    { value: 'Indiana', id: 'IN'},
    { value: 'Iowa', id: 'IA'},
    { value: 'Kansas', id: 'KS'},
    { value: 'Kentucky', id: 'KY'},
    { value: 'Louisiana', id: 'LA'},
    { value: 'Maine', id: 'ME'},
    { value: 'Marshall Islands', id: 'MH'},
    { value: 'Maryland', id: 'MD'},
    { value: 'Massachusetts', id: 'MA'},
    { value: 'Michigan', id: 'MI'},
    { value: 'Minnesota', id: 'MN'},
    { value: 'Mississippi', id: 'MS'},
    { value: 'Missouri', id: 'MO'},
    { value: 'Montana', id: 'MT'},
    { value: 'Nebraska', id: 'NE'},
    { value: 'Nevada', id: 'NV'},
    { value: 'New Hampshire', id: 'NH'},
    { value: 'New Jersey', id: 'NJ'},
    { value: 'New Mexico', id: 'NM'},
    { value: 'New York', id: 'NY'},
    { value: 'North Carolina', id: 'NC'},
    { value: 'North Dakota', id: 'ND'},
    { value: 'Northeren Mariana Islands', id: 'MP'},
    { value: 'Ohio', id: 'OH'},
    { value: 'Oklahoma', id: 'OK'},
    { value: 'Oregon', id: 'OR'},
    { value: 'Palau', id: 'PW'},
    { value: 'Pennsylvania', id: 'PA'},
    { value: 'Puerto Rico', id: 'PR'},
    { value: 'Rhode Island', id: 'RI'},
    { value: 'South Carolina', id: 'SC'},
    { value: 'South Dakota', id: 'SD'},
    { value: 'Tennessee', id: 'TN'},
    { value: 'Texas', id: 'TX'},
    { value: 'Utah', id: 'UT'},
    { value: 'Vermont', id: 'VT'},
    { value: 'Virgin Islands', id: 'VI'},
    { value: 'Virginia', id: 'VA'},
    { value: 'Washington', id: 'WA'},
    { value: 'West Virginia', id: 'WV'},
    { value: 'Wisconsin', id: 'WI'},
    { value: 'Wyoming', id: 'WY' }
];

const Dropdown: React.FC<IProps> = (props) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(false);

  const [selectedValue, setSelectedValue] = useState<IOption>(props.value);

  const selectRef:RefObject<HTMLSelectElement> = React.createRef();
  const divRef:RefObject<HTMLDivElement> = React.createRef();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const combobox = Boolean(props.searchable && props.searchPlaceholder);

  const pSelectedValue = helperService.usePrevious(selectedValue);
  const pValue = helperService.usePrevious(props.value);

  const resolvedOptions = useMemo(() => {
    const hasResultOptionSet = props.options.some((option) =>
      [
        Constants.score_result_type.qualified,
        Constants.score_result_type.not_qualified,
        Constants.score_result_type.disqualified,
        Constants.score_result_type.excused,
        Constants.score_result_type.absent,
        Constants.score_result_type.non_regular,
        Constants.score_result_type.failed,
      ].includes(option.id as any)
    );

    const isResultDropdown = props.label.toLowerCase().includes("result");
    const hasScratched = props.options.some(
      (option) => option.id === Constants.score_result_type.scratched
    );
    const hasConflict = props.options.some(
      (option) => option.id === Constants.score_result_type.conflict
    );

    if (!isResultDropdown || !hasResultOptionSet) {
      return props.options;
    }

    const extra: { id: string; value: string }[] = [];
    if (!hasScratched) {
      extra.push({ id: Constants.score_result_type.scratched, value: "Scratched" });
    }
    if (!hasConflict) {
      extra.push({ id: Constants.score_result_type.conflict, value: "Conflict" });
    }

    return extra.length > 0 ? [...props.options, ...extra] : props.options;
  }, [props.label, props.options]);

  const visibleOptions = useMemo(() => {
    const activeQuery = combobox ? (editing ? query : "") : query;
    if (!props.searchable || !activeQuery.trim()) return resolvedOptions;
    const q = activeQuery.trim().toLowerCase();
    return resolvedOptions.filter((option, index) => {
      if (index === 0 && option.id === "") return true;
      return option.value.toLowerCase().includes(q);
    });
  }, [combobox, editing, props.searchable, query, resolvedOptions]);

  useEffect(() => {
    if (!showOptions) {
      setQuery("");
      setEditing(false);
    }
  }, [showOptions]);

  useEffect(() => {
    if (!showOptions) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showOptions]);

  useEffect(() => {
    if (pSelectedValue !== selectedValue) {
      const event = new Event("change", { bubbles: true });
      selectRef?.current?.dispatchEvent(event);
    }
  }, [selectedValue, pSelectedValue, selectRef]);

  // Dropdown value being programmatically set, outside of the users input
  useEffect(() => {
    if (pValue) {
      let _pValue = pValue as IOption;

      if (props.value.id !== _pValue.id) {
        setSelectedValue(props.value);
      }
    }
  }, [props.value, selectedValue, pValue]);


  return (
    <div ref={rootRef} className={(showOptions ? "Dropdown wOptions" : props.error ? "Dropdown error" : "Dropdown") + (props.dropUp ? " dropUp" : "") + (props.alignRight ? " alignRight" : "") + (props.searchable ? " searchable" : "") + (combobox ? " combobox" : "") + (props.className ? " " + props.className : "")}>
      <label><span title={props.label}>{props.label}</span></label>
      <select disabled={props.disabled ? props.disabled : false} id={props.id} onChange={(e:any) => { e.target.value = selectedValue.id; props.onChange(e, selectedValue); }} defaultValue={selectedValue.value} ref={selectRef}>
        <option value={""}>No Value Selected</option>
        {visibleOptions.map((option:IOption, index:number) => {
          return(
            <option disabled={true} key={index} value={option.id}>{option.value}</option>
          );
        })}
      </select>
      <div ref={divRef} className={props.icon ? props.disabled ? "select disabled" : "select" : props.disabled ? "select noIcon disabled": "select noIcon"} onClick={() => { if (!props.disabled) { setShowOptions(!showOptions); } }}>
        {props.icon ? <img className="icon" src={props.icon} alt="Dropdown Icon" /> : null}
        {combobox ? (
          <input
            className="searchValue"
            placeholder={props.searchPlaceholder}
            value={editing ? query : selectedValue.value}
            disabled={props.disabled}
            onFocus={(e) => {
              setShowOptions(true);
              e.currentTarget.select();
            }}
            onChange={(e) => {
              const next = e.target.value;
              setQuery(next);
              setEditing(true);
              setShowOptions(true);
              if (!next.trim()) {
                const cleared = resolvedOptions.find((option) => option.id === "") || resolvedOptions[0];
                if (cleared) setSelectedValue(cleared);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowOptions(false);
            }}
          />
        ) : (
          <div className="value">{selectedValue.value}</div>
        )}
      </div>
      {showOptions && <div className="options">
        {props.searchable && !combobox && (
          <div className="optionSearch" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              placeholder={props.searchPlaceholder || "Search armband or name"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="innerOptions">
          {visibleOptions.map((option:IOption, index:number) => {
            return(
              <div key={index} onMouseDown={(e) => e.preventDefault()} onClick={(e:any) => { if (!option.disabled) { setSelectedValue(option); setEditing(false); setShowOptions(false); } }} className={option.disabled ? "option disabled" : "option"}>
                <div className={selectedValue.value === option.value ? "check" : "check hidden"}>
                  <img src={checkIcon} alt="Selected Option"/>
                </div>
                <span>{option.disabled ? option.value + " (disabled)" : option.value}</span>
              </div>
            );
          })}
        </div>
      </div>}
    </div>
  );
};

export default Dropdown;